import {
  ModelRegistryMigrationConfig,
  Project,
} from '../../../generated/graphql';

export enum MigrationStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export enum EntryPoint {
  GLOBAL_REGISTRY = 'GLOBAL_REGISTRY',
  LEGACY_MR = 'LEGACY_MR',
  LEGACY_MR_ACCESS_CONTROLS = 'LEGACY_MR_ACCESS_CONTROLS',
  PROJECT = 'PROJECT',
}

export type QueryModelRegistryMigrationConfig = Pick<
  ModelRegistryMigrationConfig,
  | 'blockWritesMigratedModelRegistry'
  | 'blockWritesLegacyModelRegistry'
  | 'migrationCompletedAt'
> & {orgRegistryProject?: Pick<Project, 'id' | 'entityName' | 'name'> | null};

// blockWritesLegacyModelRegistry && !blockWritesMigratedModelRegistry => Migration complete for an entity/org
// 1. If the migration is complete for the org entity (i.e., the whole org), then the status is "completed" for the individual teams too
// 2. If the migration is complete for the team entity, then the status is "completed" for that team
// 3. If the migration is in progress for a team entity, then the status is "in_progress" for that team
export const getModelRegistryMigrationStatus = (
  orgMigrationConfig?: QueryModelRegistryMigrationConfig | null,
  entityMigrationConfig?: QueryModelRegistryMigrationConfig | null
): {
  orgMigrationStatus: MigrationStatus;
  entityMigrationStatus: MigrationStatus;
} => {
  let orgMigrationStatus = MigrationStatus.NOT_STARTED;
  let entityMigrationStatus = MigrationStatus.NOT_STARTED;
  if (orgMigrationConfig == null && entityMigrationConfig == null) {
    return {orgMigrationStatus, entityMigrationStatus};
  }

  // If the organization migration is completed, so is the teams'
  if (
    orgMigrationConfig?.blockWritesLegacyModelRegistry &&
    !orgMigrationConfig?.blockWritesMigratedModelRegistry
  ) {
    return {
      orgMigrationStatus: MigrationStatus.COMPLETED,
      entityMigrationStatus: MigrationStatus.COMPLETED,
    };
  }
  if (
    orgMigrationConfig?.blockWritesLegacyModelRegistry &&
    orgMigrationConfig?.blockWritesMigratedModelRegistry
  ) {
    orgMigrationStatus = MigrationStatus.IN_PROGRESS;
  }

  if (
    entityMigrationConfig?.blockWritesLegacyModelRegistry &&
    !entityMigrationConfig?.blockWritesMigratedModelRegistry
  ) {
    entityMigrationStatus = MigrationStatus.COMPLETED;
  } else if (
    entityMigrationConfig?.blockWritesLegacyModelRegistry &&
    entityMigrationConfig?.blockWritesMigratedModelRegistry
  ) {
    entityMigrationStatus = MigrationStatus.IN_PROGRESS;
  }

  // This should not happen, but in case it does, we return a NOT_STARTED status for both the organization and the entity
  if (
    orgMigrationStatus === MigrationStatus.NOT_STARTED &&
    entityMigrationStatus !== MigrationStatus.NOT_STARTED
  ) {
    console.warn(
      `Invalid migration configuration: orgMigrationStatus: ${orgMigrationStatus}, entityMigrationStatus: ${entityMigrationStatus}`
    );
    return {
      orgMigrationStatus: MigrationStatus.NOT_STARTED,
      entityMigrationStatus: MigrationStatus.NOT_STARTED,
    };
  }

  return {
    orgMigrationStatus,
    entityMigrationStatus,
  };
};

// Though we enforce write-blocks in the backend, this method will help enforce a stricter UX
// We block writes to the legacy model registry once the migration for that team has begun, and
// continue to do so post the completion of the migration too.
export function getIsModelRegistryReadOnly(migrationStatus: MigrationStatus) {
  return (
    migrationStatus === MigrationStatus.COMPLETED ||
    migrationStatus === MigrationStatus.IN_PROGRESS
  );
}
